import { useCallback } from "react";
import dayjs, { type Dayjs } from "dayjs";
import { useSearchParams } from "react-router-dom";

import { toDayKey, type CalendarView } from "~/components/EventCalendar/calendarModel";

const VIEW_PARAM = "view";
const DATE_PARAM = "date";
const VIEWS: readonly string[] = ["month", "week", "agenda"] satisfies CalendarView[];
const DEFAULT_VIEW: CalendarView = "month";

/**
 * View and day live in the URL, next to the trainer: the popover links to the client's profile, and
 * "back" has to land on the same week instead of today's month.
 */
export const useCalendarPosition = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const requestedView = searchParams.get(VIEW_PARAM);
  const view = requestedView && VIEWS.includes(requestedView) ? (requestedView as CalendarView) : DEFAULT_VIEW;
  const requestedDate = searchParams.get(DATE_PARAM);
  const dateKey = requestedDate && dayjs(requestedDate).isValid() ? requestedDate : toDayKey(dayjs());

  const setParam = useCallback(
    (name: string, value: string | null) =>
      setSearchParams(
        (params) => {
          if (value === null) {
            params.delete(name);
          } else {
            params.set(name, value);
          }
          return params;
        },
        { replace: true },
      ),
    [setSearchParams],
  );

  return {
    view,
    dateKey,
    setView: useCallback((next: CalendarView) => setParam(VIEW_PARAM, next), [setParam]),
    setDate: useCallback((next: Dayjs | Date) => setParam(DATE_PARAM, toDayKey(next)), [setParam]),
    goToToday: useCallback(() => setParam(DATE_PARAM, null), [setParam]),
  };
};
